import { readSecretsFile, writeSecretsFile, SecretsFile } from "./file-format";
import { encryptSecret, decryptSecret } from "./crypto";
import {
  generateAndStoreIdentity,
  exportIdentity,
  importIdentity,
} from "./key-management";
import { readTeamConfig, writeTeamConfig, TeamConfig } from "./team-config";
import { DEFAULT_TEAM_PATH } from "./constants";
import fs from "fs/promises";
import fsSync from "fs";
import path from "path";
import { getRecoveryKey, setRecoveryKey } from "./team-config";

const SECRETS_PATH = path.resolve(process.cwd(), ".envfortress.secrets.enc");

/**
 * Set (or update) an encrypted secret in the secrets file.
 */
export async function setSecretCommand(
  key: string,
  value: string,
  options: any
) {
  const recipients: string[] = options.recipients;
  if (!recipients || !Array.isArray(recipients) || recipients.length === 0) {
    throw new Error("At least one recipient public key is required.");
  }

  let secretsFile: SecretsFile;
  try {
    secretsFile = await readSecretsFile(SECRETS_PATH);
  } catch (e) {
    // If file doesn't exist, initialize a new one
    secretsFile = {
      version: 1,
      secrets: {},
    };
  }

  const encrypted = await encryptSecret(value, recipients);
  secretsFile.secrets[key] = encrypted;
  await writeSecretsFile(SECRETS_PATH, secretsFile);
  console.log(
    `Secret '${key}' encrypted and saved to .envfortress.secrets.enc`
  );
}

/**
 * Get and decrypt a secret from the secrets file.
 */
export async function getSecretCommand(key: string, options: any) {
  const identity: string = options.identity;
  if (!identity) {
    throw new Error("--identity (private key) is required.");
  }
  let secretsFile: SecretsFile;
  try {
    secretsFile = await readSecretsFile(SECRETS_PATH);
  } catch (e) {
    console.error(
      `Could not read .envfortress.secrets.enc: ${
        e instanceof Error ? e.message : e
      }`
    );
    process.exit(1);
  }
  const entry = secretsFile.secrets[key];
  if (!entry) {
    console.error(`Secret '${key}' not found in .envfortress.secrets.enc`);
    process.exit(1);
  }
  // Support both string and { value } entry
  const armored = typeof entry === "string" ? entry : entry.value;
  try {
    const value = await decryptSecret(armored, identity);
    console.log(value);
  } catch (e) {
    console.error(
      `Failed to decrypt secret '${key}': ${e instanceof Error ? e.message : e}`
    );
    process.exit(1);
  }
}

/**
 * Decrypt all secrets and write them to an output file as KEY=VALUE lines.
 */
export async function decryptSecretsCommand(options: any) {
  const identity: string = options.identity;
  const output: string = options.output || ".env";
  if (!identity) {
    throw new Error("--identity (private key) is required.");
  }
  let secretsFile: SecretsFile;
  try {
    secretsFile = await readSecretsFile(SECRETS_PATH);
  } catch (e) {
    console.error(
      `Could not read .envfortress.secrets.enc: ${
        e instanceof Error ? e.message : e
      }`
    );
    process.exit(1);
  }
  const results: string[] = [];
  let successCount = 0;
  for (const [key, entry] of Object.entries(secretsFile.secrets)) {
    const armored = typeof entry === "string" ? entry : entry.value;
    try {
      const value = await decryptSecret(armored, identity);
      results.push(`${key}=${value}`);
      successCount++;
    } catch (e) {
      console.error(
        `Failed to decrypt secret '${key}': ${
          e instanceof Error ? e.message : e
        }`
      );
    }
  }
  if (results.length === 0) {
    console.error("No secrets could be decrypted.");
    process.exit(1);
  }
  await fs.writeFile(output, results.join("\n") + "\n", "utf-8");
  console.log(`Decrypted ${successCount} secrets to ${output}`);
}

/**
 * Generate and store a new age identity (private key).
 */
export async function keygenCommand(options: any) {
  const secureStore = options.secureStore || "file";
  const pathOpt = options.path;
  const result = await generateAndStoreIdentity({ secureStore, path: pathOpt });
  if (secureStore === "file") {
    console.log(`Identity generated and saved to: ${result}`);
  } else if (secureStore === "env") {
    console.log(
      `Identity generated and set in ENVFORTRESS_PRIVATE_KEY (env var).`
    );
  }
}

/**
 * Export the stored identity to a file or stdout.
 */
export async function exportKeyCommand(options: any) {
  const from = options.from || "file";
  const pathOpt = options.path;
  const output = options.output;
  await exportIdentity({ from, path: pathOpt, output });
  if (output) {
    console.log(`Identity exported to: ${output}`);
  }
}

/**
 * Import an identity from a file or string and store it.
 */
export async function importKeyCommand(options: any) {
  const secureStore = options.secureStore || "file";
  const pathOpt = options.path;
  let input = options.input;
  if (!input && options.inputFile) {
    input = await fs.readFile(options.inputFile, "utf-8");
  }
  if (!input) {
    throw new Error(
      "No input provided for identity import (use --input or --input-file)"
    );
  }
  const result = await importIdentity({ secureStore, input, path: pathOpt });
  if (secureStore === "file") {
    console.log(`Identity imported and saved to: ${result}`);
  } else if (secureStore === "env") {
    console.log(
      `Identity imported and set in ENVFORTRESS_PRIVATE_KEY (env var).`
    );
  }
}

/**
 * Add a user to the team config.
 */
export async function addUserCommand(
  email: string,
  pubkey: string,
  options: any
) {
  let config: TeamConfig;
  try {
    config = await readTeamConfig(DEFAULT_TEAM_PATH);
  } catch (e) {
    config = { team: {} };
  }
  // Warn if no recovery key
  if (!getRecoveryKey(config)) {
    console.warn(
      "[WARN] No recovery key set. Use 'envfortress secrets set-recovery-key' to add one."
    );
  }
  if (config.team[email]) {
    process.stderr.write(`User ${email} already exists in team.\n`);
    setTimeout(() => process.exit(1), 50); // Delay to flush output
    return;
  }
  config.team[email] = pubkey;
  const now = new Date().toISOString();
  config.audit = config.audit || {};
  config.audit[now] = `Added user ${email}`;
  await writeTeamConfig(config, DEFAULT_TEAM_PATH);
  console.log(`User ${email} added to team.`);
  // TODO: Trigger re-encryption for all secrets for new recipient set
  setTimeout(() => process.exit(0), 50); // Delay to flush output
  return;
}

/**
 * Remove/revoke a user from the team config.
 */
export async function revokeUserCommand(email: string, options: any) {
  let config: TeamConfig;
  try {
    config = await readTeamConfig(DEFAULT_TEAM_PATH);
  } catch (e) {
    console.error("No team config found.");
    setTimeout(() => process.exit(1), 50);
    return;
  }
  // Warn if no recovery key
  if (!getRecoveryKey(config)) {
    console.warn(
      "[WARN] No recovery key set. Use 'envfortress secrets set-recovery-key' to add one."
    );
  }
  if (!config.team[email]) {
    console.error(`User ${email} not found in team.`);
    setTimeout(() => process.exit(1), 50);
    return;
  }
  delete config.team[email];
  // Ensure team is always an object (even if empty)
  if (!config.team || typeof config.team !== "object") {
    config.team = {};
  }
  config.revoked = config.revoked || {};
  const now2 = new Date().toISOString();
  config.revoked[email] = now2;
  config.audit = config.audit || {};
  config.audit[now2] = `Revoked user ${email}`;
  await writeTeamConfig(config, DEFAULT_TEAM_PATH);
  console.log(`User ${email} revoked and removed from team.`);
  setTimeout(() => process.exit(0), 50); // Delay to flush output
  return;
  // TODO: Trigger re-encryption for all secrets for new recipient set
}

/**
 * List revoked users.
 */
export async function listRevokedCommand(options: any) {
  let config: TeamConfig;
  try {
    config = await readTeamConfig(DEFAULT_TEAM_PATH);
  } catch (e) {
    console.error("No team config found.");
    setTimeout(() => process.exit(1), 50);
    return;
  }
  const revoked = config.revoked || {};
  if (Object.keys(revoked).length === 0) {
    console.log("No revoked users.");
    return;
  }
  console.log("Revoked users:");
  for (const [email, ts] of Object.entries(revoked)) {
    console.log(`- ${email} (revoked at ${ts})`);
  }
}

/**
 * Print recovery instructions and check for recovery key.
 */
export async function recoverCommand(options: any) {
  let config: TeamConfig;
  try {
    config = await readTeamConfig(DEFAULT_TEAM_PATH);
  } catch (e) {
    console.error("No team config found.");
    setTimeout(() => process.exit(1), 50);
    return;
  }
  const recovery = getRecoveryKey(config);
  if (recovery) {
    console.log(`Recovery key is set: ${recovery.email} (${recovery.pubkey})`);
    console.log(
      "To recover access, use the recovery admin's private key to decrypt secrets."
    );
  } else {
    console.log(
      "No recovery key set. Use 'envfortress secrets set-recovery-key' to add one."
    );
  }
}

/**
 * Set the recovery key.
 */
export async function setRecoveryKeyCommand(
  email: string,
  pubkey: string,
  options: any
) {
  let config: TeamConfig;
  try {
    config = await readTeamConfig(DEFAULT_TEAM_PATH);
  } catch (e) {
    config = { team: {} };
  }
  setRecoveryKey(config, email, pubkey);
  await writeTeamConfig(config, DEFAULT_TEAM_PATH);
  console.log(`Recovery key set: ${email} (${pubkey})`);
}
